import '../styles.css'

import { ContentTabs, Vertical } from '@gravitate-js/excalibrr'
import { Modal } from 'antd'
import { useMemo, useState } from 'react'

import { ImageViewer } from '@/components/_SiteWide/ImageViewer'
import { useImageUpload } from '@/hooks/useImageUpload'
import api from '@/utils/api'
import { useQueryClient } from '@tanstack/react-query'
import { Footer } from './Footer'

type BolImageModalProps = {
  visible: boolean
  setShowingModal: (show: boolean) => void
  canEditOrderMovements: boolean
  movementDetails: Gravitate.OrderMovementDetails | undefined
  onChangeImages: (updatedImages: Gravitate.Photo[], index: number) => void
}

export function BolImageModal({
  visible,
  setShowingModal,
  canEditOrderMovements,
  movementDetails,
  onChangeImages,
}: BolImageModalProps) {
  const [selectedBolIndex, setSelectedBolIndex] = useState(0)
  const queryClient = useQueryClient()

  const selectedBol = useMemo(() => {
    return movementDetails?.bols?.[selectedBolIndex]
  }, [movementDetails?.bols, selectedBolIndex])

  const currentImages = useMemo(() => {
    return selectedBol?.photos || []
  }, [selectedBol])

  function resetImageModal() {
    resetImageSelection()
    setShowingModal(false)
  }

  async function handleDelete(image: Gravitate.Photo) {
    return api.deleteBOLImage(selectedBol?.key, image.photo).then(() => {
      if (movementDetails?.order_id !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', movementDetails.order_id] })
      }
    })
  }

  async function handleUpload(fd: FormData) {
    return api.uploadBOLImage(movementDetails?.order_id, selectedBol, fd).then(() => {
      if (movementDetails?.order_id !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', movementDetails.order_id] })
      }
    })
  }

  function handleImagesChange(updatedImages: Gravitate.Photo[]) {
    onChangeImages(updatedImages, selectedBolIndex)
  }

  const {
    images,
    selectedImageIndex,
    isUploading,
    setSelectedImageIndex,
    uploadImage,
    deleteImage,
    resetImageSelection,
  } = useImageUpload<Gravitate.Photo>({
    images: currentImages,
    uploadApi: handleUpload,
    deleteApi: handleDelete,
    onImagesChange: handleImagesChange,
  })

  return (
    <Modal
      title={`#${movementDetails?.order_number} BOL Attachments`}
      open={visible}
      classNames={{ body: 'image-modal' }}
      width='80vw'
      onCancel={resetImageModal}
      onOk={resetImageModal}
      destroyOnClose
      footer={
        <Footer
          canEditOrderMovements={canEditOrderMovements}
          bol={selectedBol}
          onCancel={resetImageModal}
          onUploadImage={uploadImage}
          isImageUploading={isUploading}
          hasImages={!!images?.length}
        />
      }
    >
      <Vertical className='pt-2'>
        <ContentTabs
          activeKey={selectedBolIndex.toString()}
          items={
            movementDetails?.bols?.map((bol, index) => ({
              key: index.toString(),
              title: 'BOL #' + bol.number,
              count: bol.photos?.length ?? 0,
            })) ?? []
          }
          onChange={(key) => {
            setSelectedBolIndex(parseInt(key))
            resetImageSelection()
          }}
        />
        <ImageViewer
          images={images}
          selectedImageIndex={selectedImageIndex}
          setSelectedImageIndex={setSelectedImageIndex}
          onDelete={deleteImage}
          onUpload={uploadImage}
          isImageUploading={isUploading}
          canEdit={canEditOrderMovements}
        />
      </Vertical>
    </Modal>
  )
}
